import React, { lazy } from 'react';
import {
  mdiAccountMultiple,
  mdiCheckboxMarkedCircleOutline,
  mdiCurrencyUsd,
  mdiFileDocumentEditOutline,
  mdiFormSelect,
  mdiFormatListBulleted,
  mdiClockOutline,
  mdiAccountTie,
  mdiFormDropdown,
  mdiHomeCity,
} from '@mdi/js';

const LoanProgram = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/LoanProgram'),
);
const LoanInfo = lazy(
  () => import('src/components/loanOfficer/Loan//Forms/Residential/LoanInfo'),
);
const LoanTerms = lazy(
  () => import('src/components/loanOfficer/Loan//Forms/Residential/LoanTerms'),
);
const BorrowerInfo = lazy(
  () => import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo'),
);

const PersonalInformation = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/PersonalInformation'),
);
const IndividualSectionForPair = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/IndividualSectionForPair'),
);
const IndividualSection = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/IndividualSection'),
);
const URLAAlternateNames = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/AlternateNames'),
);
const Assets = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/Assets'),
);
const OtherAssets = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/OtherAssets'),
);
const Liabilities = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/Liabilities'),
);
const OtherLiabilities = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/OtherLiabilities'),
);
const Employments = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/Employments'),
);
const OtherIncomes = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/OtherIncomes'),
);
const GiftsGrants = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/GiftsGrants'),
);
const ReoProperties = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/ReoProperties'),
);
const AdditionalLoans = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/AdditionalLoans'),
);
const Residences = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/Residences'),
);
const MailingAddress = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/MailingAddress'),
);
const Declarations = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/Declarations'),
);
const Demographic = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/Demographic'),
);
const JuniorLienExceptionRequest = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/JuniorLien'),
);
const Expiration = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/Expiration'),
);
const ClosingDisclosureCheck = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/ClosingDisclosureCheck'),
);
const Lender = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Residential/Lender'),
);

// Lender Individual Components
const LenderSectionWrapper = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/Lender/LenderSectionWrapper'),
);
const LenderBasicInfo = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/Lender/BasicInfo'),
);
const LenderPropertyAndLoanInfo = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/Lender/PropertyAndLoanInfo'),
);
const LenderMortgageLoanInformation = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/Lender/MortgageLoanInformation'),
);
const LenderTitleInformation = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/Lender/TitleInformation'),
);
const LenderQualifyingTheBorrower = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/Lender/QualifyingTheBorrower'),
);

const LenderHomeownershipEducation = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/Lender/HomeownershipEducation'),
);

const NonQMExceptionRequest = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/NonQMExceptionRequest'),
);

const Itemization = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/Itemization'),
);

const AffordableTenant = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Residential/AffordableTenant'),
);

export const formSections = [
  {
    title: 'Loan Program',
    route: 'loan-program',
    icon: mdiFormSelect,
    component: <LoanProgram />,
    requiresLoanProgram: false,
  },
  {
    title: 'Loan Info',
    route: 'loan-info',
    icon: mdiFileDocumentEditOutline,
    component: <LoanInfo />,
    requiresLoanProgram: true,
  },
  {
    title: 'Request Exception',
    route: 'juniorlien-exception',
    icon: mdiCheckboxMarkedCircleOutline,
    component: <JuniorLienExceptionRequest />,
    requiresLoanProgram: true,
    juniorLienOnly: true,
  },
  {
    title: 'Loan Terms',
    route: 'loan-terms',
    icon: mdiCurrencyUsd,
    component: <LoanTerms />,
    requiresLoanProgram: true,
  },
  {
    title: 'Borrower Info',
    route: 'borrower-info',
    icon: mdiAccountMultiple,
    component: <BorrowerInfo />,
    requiresLoanProgram: true,
    applicationDependent: true,
    sections: [
      {
        title: 'Personal Information',
        route: 'information',
        component: (
          <IndividualSectionForPair
            Component={PersonalInformation}
            title='Personal Information'
          />
        ),
      },
      {
        title: 'Alternate Names',
        route: 'alternate-names',
        component: (
          <IndividualSectionForPair
            Component={URLAAlternateNames}
            title='Alternate Names'
          />
        ),
      },
      {
        title: 'Residences',
        route: 'residences',
        component: (
          <IndividualSectionForPair Component={Residences} title='Residences' />
        ),
      },
      {
        title: 'Mailing Address',
        route: 'mailing-address',
        component: (
          <IndividualSectionForPair
            Component={MailingAddress}
            title='Mailing Address'
          />
        ),
      },
      {
        title: 'Assets',
        route: 'assets',
        component: (
          <IndividualSectionForPair Component={Assets} title='Account Assets' />
        ),
      },
      {
        title: 'Other Assets',
        route: 'other-assets',
        component: (
          <IndividualSectionForPair
            Component={OtherAssets}
            title='Physical Assets'
          />
        ),
      },
      {
        title: 'Liabilities',
        route: 'liabilities',
        component: (
          <IndividualSectionForPair
            Component={Liabilities}
            title='Liabilities'
          />
        ),
      },
      {
        title: 'Other Liabilities',
        route: 'other-liabilities',
        component: (
          <IndividualSectionForPair
            Component={OtherLiabilities}
            title='Other Liabilities'
          />
        ),
      },
      {
        title: 'Employments',
        route: 'employments',
        component: (
          <IndividualSectionForPair
            Component={Employments}
            title='Employments'
          />
        ),
      },
      {
        title: 'Other Incomes',
        route: 'other-incomes',
        component: (
          <IndividualSectionForPair
            Component={OtherIncomes}
            title='Other Incomes'
          />
        ),
      },
      {
        title: 'Gifts & Grants',
        route: 'gifts-grants',
        component: (
          <IndividualSectionForPair
            Component={GiftsGrants}
            title='Gifts & Grants'
          />
        ),
      },
      {
        title: 'REO Properties',
        route: 'reo-properties',
        component: (
          <IndividualSectionForPair
            Component={ReoProperties}
            title='REO Properties'
          />
        ),
      },
      {
        title: 'Additional Loans',
        route: 'additional-loans',
        component: (
          <IndividualSectionForPair
            Component={AdditionalLoans}
            title='Additional Loans'
          />
        ),
      },
      {
        title: 'Declarations',
        route: 'declarations',
        component: (
          <IndividualSection Component={Declarations} title='Declarations' />
        ),
      },
      {
        title: 'Demographic',
        route: 'demographic',
        component: (
          <IndividualSectionForPair
            Component={Demographic}
            title='Demographic'
          />
        ),
      },
    ],
  },
  {
    title: 'Lender',
    route: 'lender',
    icon: mdiAccountTie,
    requiresLoanProgram: true,
    component: <Lender />,
    sections: [
      {
        title: 'Basic',
        route: 'basic',
        component: (
          <LenderSectionWrapper Component={LenderBasicInfo} title={'Lender'} />
        ),
      },
      {
        title: 'Property and Loan Info',
        route: 'property-and-loan-info',
        component: (
          <LenderSectionWrapper
            Component={LenderPropertyAndLoanInfo}
            title={'L1. Property and Loan Info'}
          />
        ),
      },
      {
        title: 'Title Info',
        route: 'title-info',
        component: (
          <LenderSectionWrapper
            Component={LenderTitleInformation}
            title={'L2. Title Information'}
          />
        ),
      },
      {
        title: 'Mortgage Loan Info',
        route: 'mortgage-loan-info',
        component: (
          <LenderSectionWrapper
            Component={LenderMortgageLoanInformation}
            title={'L3. Mortgage Loan Information'}
          />
        ),
      },
      {
        title: 'Qualifying the Borrower',
        route: 'qualifying-the-borrower',
        component: (
          <LenderSectionWrapper
            Component={LenderQualifyingTheBorrower}
            title={
              'L4. Qualifying the Borrower - Minimum Required Funds or Cash Back'
            }
          />
        ),
      },
      {
        title: 'Homeownership Education',
        route: 'homeownership-education',
        component: (
          <IndividualSectionForPair
            Component={LenderHomeownershipEducation}
            title='L5. Homeownership Education and Housing Counseling'
          />
        ),
      },
    ],
  },

  {
    title: 'Expiration',
    route: 'expiration',
    icon: mdiClockOutline,
    component: <Expiration />,
  },
  {
    title: 'Itemization',
    route: 'itemization',
    icon: mdiFormatListBulleted,
    component: <Itemization />,
    requiresLoanProgram: true,
  },
  {
    title: 'Closing Fee Sheet',
    route: 'cd-checklist',
    icon: mdiCurrencyUsd,
    component: <ClosingDisclosureCheck />,
    requiresLoanProgram: true,
  },
  {
    title: 'NonQM Exception Request',
    route: 'nonQM-Request',
    icon: mdiFormDropdown,
    component: <NonQMExceptionRequest />,
    requiresLoanProgram: true,
  },
  {
    title: 'Affordable Tenant',
    route: 'affordable-tenant',
    icon: mdiHomeCity,
    component: <AffordableTenant />,
    requiresLoanProgram: true,
    affordableTenantOnly: true,
  },
];
